import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PatientListTable } from "../../../components/patients/PatientListTable";
import { CreateGroupModal } from "../../../components/patients/CreateGroupModal";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2, Search, Clipboard } from "lucide-react";
import { useClinics } from "@/hooks/clinics/useClinics";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import { patientsGraphqlService } from "@/services/api/patients/patientsGraphqlService";
import { z } from "zod";
import { API_CONFIG } from "@/config/envConfig";

// Mock data for patients - same as CreateGroupByFilters
const mockPatients = [
  {
    id: "1",
    patientName: "Abdi Hussein",
    emr: "124567",
    gender: "Male",
    dateOfBirth: "1962-03-10",
    age: 61,
    phoneNumber: "703-817-0000",
    providerDetails: "Arun Kirshnamurthy",
    clinicDetails: "Riveroaks Clinic",
    primaryDx: "COPD",
    secondaryDx: "Asthma",
    insuranceType: "Medicare"
  },
  {
    id: "2",
    patientName: "Abdi Hussein",
    emr: "124567",
    gender: "Male",
    dateOfBirth: "1962-03-10",
    age: 61,
    phoneNumber: "703-817-0000",
    providerDetails: "Arun Kirshnamurthy",
    clinicDetails: "Riveroaks Clinic",
    primaryDx: "CHF",
    secondaryDx: "Type 2 Diabetes",
    insuranceType: "Medicare Advantage"
  },
  {
    id: "3",
    patientName: "Abdi Hussein",
    emr: "124567",
    gender: "Male",
    dateOfBirth: "1962-03-10",
    age: 61,
    phoneNumber: "703-817-0000",
    providerDetails: "Arun Kirshnamurthy",
    clinicDetails: "Riveroaks Clinic",
    primaryDx: "Type 2 Diabetes",
    secondaryDx: "CHF",
    insuranceType: "Medicare"
  },
  {
    id: "4",
    patientName: "Abdi Hussein",
    emr: "124567",
    gender: "Male",
    dateOfBirth: "1962-03-10",
    age: 61,
    phoneNumber: "703-817-0000",
    providerDetails: "Arun Kirshnamurthy",
    clinicDetails: "Riveroaks Clinic",
    primaryDx: "Asthma",
    secondaryDx: "COPD",
    insuranceType: "Medicare Advantage"
  }
];

const CreateGroupByEMRs = () => {
  const [selectedClinic, setSelectedClinic] = useState("");
  const [clinicSearchOpen, setClinicSearchOpen] = useState(false);
  const [emrInput, setEmrInput] = useState("");
  const [displayPatients, setDisplayPatients] = useState<Array<{
    id: string;
    patientName: string;
    emr: string;
    gender: string;
    dateOfBirth: string;
    age: number;
    phoneNumber: string;
    providerDetails: string;
    clinicDetails: string;
    primaryDx: string;
    secondaryDx: string;
    insuranceType: string;
  }>>([]);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;
  const [searchEmrs, setSearchEmrs] = useState<string[]>([]);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { clinics, loading: clinicsLoading } = useClinics(1000);
  const { hasuraToken, user, token } = useAuth();
  useEffect(() => {
    document.title = "Create New Group By EMRs | Admin";
  }, []);

  const handleGoBack = () => {
    navigate("/admin/patient-groups");
  };

  const handlePasteClick = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        // Clean extra spaces and normalize
        const cleaned = text
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .trim();
        setEmrInput(cleaned);
        toast({
          title: "Pasted Successfully",
          description: "EMR numbers have been pasted and cleaned.",
        });
      }
    } catch (error) {
      toast({
        title: "Paste Failed",
        description: "Unable to read from clipboard. Please paste manually using Ctrl+V or Cmd+V.",
        variant: "destructive",
      });
    }
  };

  const handleApply = async () => {
    if (!selectedClinic) {
      toast({
        title: "Clinic Required",
        description: "Please select a clinic first.",
        variant: "destructive",
      });
      return;
    }

    if (!emrInput.trim()) {
      toast({
        title: "EMRs Required",
        description: "Please enter at least one EMR number.",
        variant: "destructive",
      });
      return;
    }

    // Parse and validate EMR IDs (digits only, max 500)
    const rawItems = emrInput
      .split(/[\,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (rawItems.length === 0) {
      toast({
        title: "Invalid Format",
        description: "Please enter EMR numbers separated by commas.",
        variant: "destructive",
      });
      return;
    }

    // Check for invalid characters (only alphanumeric allowed)
    const invalidEmrs = rawItems.filter(item => !/^[a-zA-Z0-9]+$/.test(item));
    if (invalidEmrs.length > 0) {
      toast({
        title: "Invalid Format",
        description: `Invalid EMR format detected: "${invalidEmrs[0]}". Only alphanumeric characters (letters and numbers) are allowed.`,
        variant: "destructive",
      });
      return;
    }

    const emrSchema = z
      .array(z.string().regex(/^[a-zA-Z0-9]+$/i, { message: "EMR must be alphanumeric" }).min(1).max(30))
      .min(1, { message: "Enter at least one EMR" })
      .max(500, { message: "Too many EMRs (max 500)" });

    const parsed = emrSchema.safeParse(rawItems);
    if (!parsed.success) {
      toast({
        title: "Invalid EMR Input",
        description: parsed.error.errors[0]?.message || "Please enter comma-separated alphanumeric EMR IDs.",
        variant: "destructive",
      });
      return;
    }

    const uniqueEmrs = Array.from(new Set(parsed.data));

    if (!hasuraToken) {
      toast({
        title: "Authentication Error",
        description: "Please log in again.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      setPage(1);
      setSearchEmrs(uniqueEmrs);

      const resp = await patientsGraphqlService.getPatientsByEmrList(
        hasuraToken,
        parseInt(selectedClinic, 10),
        uniqueEmrs,
        PAGE_SIZE,
        0
      );

      const batch = (resp as any)?.data?.pep_vw_patient_details || [];
      const totalRecords =
        (resp as any)?.data?.pep_vw_patient_details_aggregate?.aggregate?.count ?? batch.length;

      const transformed = batch.map((patient: any) => {
        const age = patient.dob
          ? new Date().getFullYear() - new Date(patient.dob).getFullYear()
          : 0;
        const genderDisplay =
          patient.gender === "M" ? "Male" : patient.gender === "F" ? "Female" : patient.gender || "";
        return {
          id: patient.enrollment_patient_id?.toString() || patient.emr_patient_clinic_data_id?.toString() || "",
          patientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
          emr: patient.emr_id || "",
          gender: genderDisplay,
          dateOfBirth: patient.dob || "",
          age,
          phoneNumber: patient.mobile_number || patient.home_number || patient.work_phone || "",
          providerDetails: patient.provider_name?.trim() || "-",
          clinicDetails: patient.clinic_name || "",
          primaryDx: "-",
          secondaryDx: "-",
          insuranceType: "-",
        };
      });

      setDisplayPatients(transformed);
      setTotalCount(totalRecords);
      setSelectedPatients(transformed.map((p: any) => p.id));

      toast({
        title: "Search Complete",
        description: `Found ${totalRecords} patient${totalRecords !== 1 ? 's' : ''}.`,
      });
    } catch (error) {
      console.error("Error fetching patients:", error);
      toast({
        title: "Error",
        description: "Failed to fetch patients. Please try again.",
        variant: "destructive",
      });
      setDisplayPatients([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = async (nextPage: number) => {
    if (!hasuraToken || !selectedClinic || searchEmrs.length === 0) return;
    setIsLoading(true);
    try {
      const offset = (nextPage - 1) * PAGE_SIZE;
      const resp = await patientsGraphqlService.getPatientsByEmrList(
        hasuraToken,
        parseInt(selectedClinic, 10),
        searchEmrs,
        PAGE_SIZE,
        offset
      );
      const batch = (resp as any)?.data?.pep_vw_patient_details || [];
      const transformed = batch.map((patient: any) => {
        const age = patient.dob ? new Date().getFullYear() - new Date(patient.dob).getFullYear() : 0;
        const genderDisplay = patient.gender === "M" ? "Male" : patient.gender === "F" ? "Female" : patient.gender || "";
        return {
          id: patient.enrollment_patient_id?.toString() || patient.emr_patient_clinic_data_id?.toString() || "",
          patientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
          emr: patient.emr_id || "",
          gender: genderDisplay,
          dateOfBirth: patient.dob || "",
          age,
          phoneNumber: patient.mobile_number || patient.home_number || patient.work_phone || "",
          providerDetails: patient.provider_name?.trim() || "-",
          clinicDetails: patient.clinic_name || "",
          primaryDx: "-",
          secondaryDx: "-",
          insuranceType: "-",
        };
      });
      setDisplayPatients(transformed);
      setSelectedPatients(transformed.map((p: any) => p.id));
      setPage(nextPage);
    } catch (error) {
      console.error("Error fetching page:", error);
      toast({
        title: "Error",
        description: "Failed to fetch the selected page.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedClinic("");
    setEmrInput("");
    setDisplayPatients([]);
    setSelectedPatients([]);
    setHasSearched(false);
  };

  const handlePatientSelect = (patientId: string, checked: boolean) => {
    if (checked) {
      setSelectedPatients(prev => [...prev, patientId]);
    } else {
      setSelectedPatients(prev => prev.filter(id => id !== patientId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPatients(displayPatients.map(patient => patient.id));
    } else {
      setSelectedPatients([]);
    }
  };

  const handleCreateGroup = () => {
    if (selectedPatients.length === 0) {
      toast({
        title: "No Patients Selected",
        description: "Please select at least one patient to create a group.",
        variant: "destructive",
      });
      return;
    }

    setIsCreateGroupModalOpen(true);
  };

  const handleSaveGroup = async (groupData: { name: string; type: string; note: string }) => {
    if (!user?.id || !token || !selectedClinic) {
      toast({
        title: "Error",
        description: "Missing authentication or clinic information.",
        variant: "destructive",
      });
      return;
    }

    if (selectedPatients.length === 0) {
      toast({
        title: "Error",
        description: "No patients selected.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const endpoint = `/api/ngpep/${user.id}/patient-group`;
      const payload = {
        groupName: groupData.name,
        groupFilters: JSON.stringify({
          clinic_id: parseInt(selectedClinic, 10)
        }),
        isArchived: false,
        patientIds: selectedPatients.map(id => parseInt(id, 10))
      };

      const response = await fetch(`${API_CONFIG.API_BASE}/api/ngpep/${user.id}/patient-group`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create group: ${response.statusText}`);
      }

      toast({
        title: "Group Created Successfully",
        description: `Created "${groupData.name}" with ${selectedPatients.length} patients.`,
      });
      navigate("/admin/patient-groups");
    } catch (error: any) {
      console.error("Error creating group:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create group. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="space-y-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleGoBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Patient Group List
          </Button>
          <span className="text-muted-foreground">&gt;&gt;</span>
          <span className="text-muted-foreground">Create New Group</span>
        </div>
        
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Create New Group By EMR</h1>
          </div>
          <Button onClick={handleCreateGroup} className="bg-primary hover:bg-primary/90 px-6">
            Create Group
          </Button>
        </div>
      </header>

      <main className="space-y-6">
        <div className="bg-background border rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-8 gap-6 mb-6">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="clinic" className="text-sm font-medium">
                Select Clinic*
              </Label>
              {clinicsLoading ? (
                <div className="flex items-center justify-center h-10 border rounded-md bg-background">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                  <span className="text-sm text-muted-foreground">Loading clinics...</span>
                </div>
              ) : (
                <Popover open={clinicSearchOpen} onOpenChange={setClinicSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={clinicSearchOpen}
                      className="w-full justify-between"
                    >
                      {selectedClinic
                        ? clinics.find((clinic) => clinic.clinic_id.toString() === selectedClinic)?.clinic_name
                        : "Select a clinic"}
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0 z-50 bg-background">
                    <Command>
                      <CommandInput placeholder="Search clinic..." />
                      <CommandList>
                        <CommandEmpty>No clinic found.</CommandEmpty>
                        <CommandGroup>
                          {clinics.map((clinic) => (
                            <CommandItem
                              key={clinic.clinic_id}
                              value={clinic.clinic_name}
                              onSelect={() => {
                                setSelectedClinic(clinic.clinic_id.toString());
                                setClinicSearchOpen(false);
                              }}
                            >
                              {clinic.clinic_name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>

            <div className="space-y-2 col-span-6">
              <div className="flex items-center justify-between">
                <Label htmlFor="emrs" className="text-sm font-medium">
                  Patient EMRs* <span className="text-xs text-muted-foreground font-normal">(comma separated)</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handlePasteClick}
                  className="h-7 gap-2"
                >
                  <Clipboard className="h-3.5 w-3.5" />
                  Paste EMRs
                </Button>
              </div>
              <Textarea
                id="emrs"
                value={emrInput}
                rows={1}
                onChange={(e) => setEmrInput(e.target.value)}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('text');
                  // Clean extra spaces and normalize
                  const cleaned = pastedText
                    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                    .trim();
                  setEmrInput(cleaned);
                }}
                placeholder="Enter EMR IDs separated by commas. Ex: 101, 323SA, A1B2C3"
                className="min-h-[40px] [field-sizing:content] resize-none max-h-48"
              />
              <p className="text-xs text-muted-foreground">
                Enter alphanumeric EMR IDs separated by commas (e.g., 101, 323SA, A1B2). Maximum 500 EMRs.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={handleCancel}
              className="px-6"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleApply}
              className="bg-primary hover:bg-primary/90 px-6"
            >
              Apply
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : hasSearched && displayPatients.length > 0 ? (
          <PatientListTable
            patients={displayPatients}
            selectedPatients={selectedPatients}
            onPatientSelect={handlePatientSelect}
            onSelectAll={handleSelectAll}
            filteredCount={totalCount}
            currentPage={page}
            totalPages={Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
            onPageChange={handlePageChange}
          />
        ) : hasSearched && displayPatients.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No patients found matching the search criteria.</p>
          </div>
        ) : null}
      </main>

      <CreateGroupModal
        open={isCreateGroupModalOpen}
        onOpenChange={setIsCreateGroupModalOpen}
        onSave={handleSaveGroup}
        selectedPatientsCount={selectedPatients.length}
      />
    </div>
  );
};

export default CreateGroupByEMRs;